import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { API_BASE_URL } from '../config';
import { getAuthFetchOptions } from '../utils/auth';
import { useAuth } from './AuthContext';

const DepartmentContext = createContext();

export const DEFAULT_DEPARTMENTS = [
  { id: 'all', name: 'All Departments', icon: 'Business' }
];

export const ENVIRONMENTS = [
  { id: 'all', name: 'All Environments' },
  { id: 'production', name: 'Production' },
  { id: 'staging', name: 'Staging' },
  { id: 'development', name: 'Development' },
];

export function DepartmentProvider({ children }) {
  const { currentUser } = useAuth();
  const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS);
  const [loading, setLoading] = useState(true);

  const [selectedDepartment, setSelectedDepartment] = useState(() => {
    return localStorage.getItem('cloudcor_dept') || 'all';
  });
  
  const [selectedEnvironment, setSelectedEnvironment] = useState(() => {
    return localStorage.getItem('cloudcor_env') || 'all';
  });

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const userKey = currentUser?.id || currentUser?.email || currentUser?.username || '';

  const fetchDepartments = useCallback(async () => {
    if (!userKey) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/admin/departments`, getAuthFetchOptions());
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.departments || []);
        const mapped = list.map(d => ({ ...d, id: d.id.toString(), icon: 'Business' }));
        setDepartments([DEFAULT_DEPARTMENTS[0], ...mapped]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  }, [userKey]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    localStorage.setItem('cloudcor_dept', selectedDepartment);
  }, [selectedDepartment]);

  useEffect(() => {
    localStorage.setItem('cloudcor_env', selectedEnvironment);
  }, [selectedEnvironment]);

  // Command palette shortcut key listener (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const value = {
    selectedDepartment,
    setSelectedDepartment,
    selectedEnvironment,
    setSelectedEnvironment,
    commandPaletteOpen,
    setCommandPaletteOpen,
    departments,
    refreshDepartments: fetchDepartments,
    environments: ENVIRONMENTS,
    currentDepartmentObj: departments.find((d) => d.id === selectedDepartment) || departments[0],
    loading
  };

  return (
    <DepartmentContext.Provider value={value}>
      {children}
    </DepartmentContext.Provider>
  );
}

export function useDepartment() {
  const context = useContext(DepartmentContext);
  if (!context) {
    return {
      selectedDepartment: 'all',
      setSelectedDepartment: () => {},
      selectedEnvironment: 'all',
      setSelectedEnvironment: () => {},
      commandPaletteOpen: false,
      setCommandPaletteOpen: () => {},
      departments: DEFAULT_DEPARTMENTS,
      environments: ENVIRONMENTS,
      currentDepartmentObj: DEFAULT_DEPARTMENTS[0],
      loading: false
    };
  }
  return context;
}
